import React, { useState } from 'react';
import Card from '../../../../components/ui/card';
import Button from '../../../../components/ui/button';
import Input from '../../../../components/ui/input';
import MapboxPlacesAutocomplete from '../../../../components/ui/mapbox-places-autocomplete';
import { MapPin, Building, Home, Edit3, Check, X } from 'lucide-react';

const AddressCard = ({ client, clientDataHook }) => {
	const { updateField, isEditMode, toggleEditMode, cancelEdit } =
		clientDataHook;
	const [editValues, setEditValues] = useState({});

	const addressFields = [
		{
			key: 'officeAddress',
			label: 'Office Address',
			icon: Building,
			value: client?.officeAddress,
			placeholder: 'Enter Office Address...',
		},
		{
			key: 'homeAddress',
			label: 'Home Address',
			icon: Home,
			value: client?.homeAddress,
			placeholder: 'Enter Home Address...',
		},
	];

	const handleEdit = (fieldKey) => {
		const field = addressFields.find((f) => f.key === fieldKey);
		const currentValue = field?.value;

		// Initialize edit value with the current address object
		// MapboxPlacesAutocomplete expects an object, not a string
		let editValue;
		if (typeof currentValue === 'object' && currentValue !== null) {
			editValue = currentValue;
		} else if (typeof currentValue === 'string' && currentValue.trim()) {
			// Convert string to object format
			editValue = {
				formatted_address: currentValue,
				street: currentValue,
				city: '',
				state: '',
				zip: '',
				coordinates: null,
			};
		} else {
			// Empty/null value
			editValue = {
				formatted_address: '',
				street: '',
				city: '',
				state: '',
				zip: '',
				coordinates: null,
			};
		}

		setEditValues({
			...editValues,
			[fieldKey]: editValue,
		});

		toggleEditMode(fieldKey);
	};

	const handleSave = async (fieldKey) => {
		const addressValue = editValues[fieldKey];
		let valueToSave;

		// Check if address has any meaningful content
		if (
			addressValue &&
			(addressValue.formatted_address?.trim() ||
				addressValue.street?.trim() ||
				addressValue.city?.trim())
		) {
			valueToSave = addressValue;
		} else {
			// If empty, save null to clear the field
			valueToSave = null;
		}

		const success = await updateField(fieldKey, valueToSave);
		if (success) {
			// Clear edit state
			const newEditValues = { ...editValues };
			delete newEditValues[fieldKey];
			setEditValues(newEditValues);
		}
	};

	const handleCancel = (fieldKey) => {
		// Clear edit state
		const newEditValues = { ...editValues };
		delete newEditValues[fieldKey];
		setEditValues(newEditValues);
		cancelEdit(fieldKey);
	};

	const handleAddressChange = (fieldKey, address) => {
		setEditValues({
			...editValues,
			[fieldKey]: address,
		});
	};

	const formatAddress = (addressObj) => {
		if (!addressObj) return '';

		// Handle string values
		if (typeof addressObj === 'string') {
			return addressObj;
		}

		// Handle non-object values (e.g., null, undefined, numbers)
		if (typeof addressObj !== 'object' || addressObj === null) {
			return '';
		}

		// Handle object with formatted_address
		if (
			addressObj.formatted_address &&
			typeof addressObj.formatted_address === 'string'
		) {
			return addressObj.formatted_address;
		}

		// Build formatted address from components
		const parts = [];
		if (addressObj.street && typeof addressObj.street === 'string') {
			parts.push(addressObj.street);
		}
		if (addressObj.city && typeof addressObj.city === 'string') {
			parts.push(addressObj.city);
		}
		if (addressObj.state && typeof addressObj.state === 'string') {
			parts.push(addressObj.state);
		}
		if (addressObj.zip && typeof addressObj.zip === 'string') {
			parts.push(addressObj.zip);
		}

		const result = parts.length > 0 ? parts.join(', ') : '';

		// Ensure we return a string, never an object
		if (typeof result !== 'string') {
			return '';
		}

		return result;
	};

	const renderAddressField = (field) => {
		const isEditing = isEditMode[field.key];

		// Check if field has value - handle both string and object types
		const hasValue =
			field.value &&
			(typeof field.value === 'string'
				? field.value.trim() !== ''
				: field.value.formatted_address || field.value.street);

		// Conditional styling: white background for filled fields, gray for unfilled
		const cardBgClass = hasValue ? 'bg-white shadow-sm' : 'bg-stone-50/50';

		return (
			<div
				key={field.key}
				className={`${cardBgClass} border border-gray-100 rounded-lg p-3 sm:p-4 transition-all duration-200 hover:border-primary/30`}>
				<div className='flex items-start justify-between'>
					<div className='flex-1'>
						<label className='flex items-center text-xs font-montserrat-medium uppercase tracking-wide text-gray-400 mb-1.5'>
							<field.icon className='w-3.5 h-3.5 mr-1.5 text-primary/30' />
							{field.label}
						</label>

						{isEditing ? (
							<div className='space-y-3'>
								<MapboxPlacesAutocomplete
									id={field.key}
									name={field.key}
									value={editValues[field.key]}
									onChange={(address) =>
										handleAddressChange(field.key, address)
									}
									placeholder={field.placeholder}
									className='w-full'
								/>

								<div className='flex items-center space-x-2'>
									<Button size='sm' onClick={() => handleSave(field.key)}>
										<Check className='w-4 h-4 mr-1' />
										Save
									</Button>
									<Button
										size='sm'
										variant='outline'
										onClick={() => handleCancel(field.key)}>
										<X className='w-4 h-4 mr-1' />
										Cancel
									</Button>
								</div>
							</div>
						) : (
							<div className='space-y-2'>
								<div className='text-sm font-montserrat-medium text-gray-900'>
									{(() => {
										const formattedAddress = formatAddress(field.value);
										const safeAddress =
											typeof formattedAddress === 'string'
												? formattedAddress
												: '';
										return safeAddress ? (
											<span>{safeAddress}</span>
										) : (
											<span className='text-gray-300 italic font-montserrat-regular'>
												Not provided
											</span>
										);
									})()}
								</div>
								{field.value?.coordinates &&
									typeof field.value.coordinates.lat === 'number' &&
									typeof field.value.coordinates.lng === 'number' && (
										<div className='text-xs text-gray-500'>
											📍 Coordinates: {field.value.coordinates.lat.toFixed(6)},{' '}
											{field.value.coordinates.lng.toFixed(6)}
										</div>
									)}
							</div>
						)}
					</div>

					{!isEditing && (
						<Button
							size='sm'
							variant='ghost'
							onClick={() => handleEdit(field.key)}
							aria-label={`Edit ${field.label}`}
							className='ml-2 text-gray-300 hover:text-primary flex-shrink-0 transition-colors duration-200'>
							<Edit3 className='w-4 h-4' />
						</Button>
					)}
				</div>
			</div>
		);
	};

	return (
		<Card className='border-l-2 border-l-primary/20'>
			<Card.Header className='border-b border-gray-100'>
				<Card.Title className='flex items-center text-sm sm:text-base font-glacial uppercase tracking-wider text-gray-900'>
					<MapPin className='w-4 h-4 mr-2 text-primary' />
					Addresses
				</Card.Title>
			</Card.Header>
			<Card.Content>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					{addressFields.map(renderAddressField)}
				</div>
			</Card.Content>
		</Card>
	);
};

export default AddressCard;
